import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputLabel,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { CustomEllipticalButton, CustomInput } from "Components/Form";
import { Colors } from "Utils/colors";
import { useFormik } from "formik";
import { isValidString } from "Utils/validators";
import { formatAsUsPhoneNo } from "Utils/transform";
import * as Yup from "yup";
import { SnackBarContext } from "Context/SnackContext";
import { useContext } from "react";
import debounce from "lodash.debounce";
import { useState, useEffect, useCallback } from "react";
import { Customer } from "Features/services/customer.api";
import { getAccessToken } from "Features/slice/customer/customer.Slice";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { InFlow } from "Features/services/inFlow.api";
import { v4 as uuidv4 } from "uuid";
import DatePicker from "react-datepicker";
import { Invoices } from "Features/services/invoice.api";

const quickOrderSchema = Yup.object().shape({
  display_name: Yup.string().required("Customer name is required"),
  phoneNo: Yup.string()
    .required("Phone number is required")
    .matches(/[0-9()-]/g, "Invalid phone number"),
  email: Yup.string().email().required("Email is required"),
  address: Yup.string().required("Address is required"),
  products: Yup.array().required("Products are required"),
  shippingOptions: Yup.string().required("Shipping type is required"),
  paymentType: Yup.string().required("Payment type is required"),
});

const productsInitialValue = {
  id: "",
  item: "",
  qty: 0,
  price: 0,
};

const initialValues = {
  invoiceNo: "",
  date: new Date(),
  status: "In Process",
  display_name: "",
  email: "",
  phoneNo: "",
  qb_customer_id: "",
  address: "",
  city: "",
  country: "",
  zip: "",
  shippingOptions: "International",
  paymentType: "UPI",
  products: [productsInitialValue],
};

export default function QuickOrderDashboard() {
  const { handleSnackbar } = useContext(SnackBarContext);

  const dispatch = useAppDispatch();
  const { access_token } = useAppSelector((state: any) => state.customer);

  const [showCalendar, setShowCalendar] = useState(false);

  // states for customer search
  const [customerQuery, setCustomerQuery] = useState<string>("");
  const [customerSearchResult, setCustomerSearchResult] = useState<any>([]);
  const [customerLoading, setCustomerLoading] = useState<boolean>(false);
  const [customerDropDown, setCustomerDropDown] = useState<boolean>(false);

  // states for porducts search
  const [productQuery, setProductQuery] = useState<string>("");
  const [productSearchResult, setProductSearchResult] = useState<any>(false);
  const [productLoading, setProductLoading] = useState<boolean>(false);
  const [productsDropDown, setProductsDropDown] = useState<boolean>(false);


  const quickOrderForm = useFormik({

    initialValues,
    validationSchema: quickOrderSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
    
      const invoiceInfo = {
        invoiceNo: values.invoiceNo,
        date: values.date,
        status: values.status,
      };
      const userInfo = {
        display_name: values.display_name,
        email: values.email,
        quickbook_customer_id: values.qb_customer_id,
        phoneNo: values.phoneNo,
      };

      const BillAddr = {
        Line1: values.address,
        City: values.city,
        CountrySubDivisionCode: values.country,
        PostalCode: values.zip,
      };

      const payload = {
        access_token,
        userInfo,
        invoiceInfo,
        products: values.products,
        BillAddr,
      };

      // Submit the payload
      try {
        const response = await Invoices.instance.createInvoice(payload);
        handleSnackbar(true, `Invoice created successfully`, "success");
        resetFormDetails();
      } catch (error: any) {
        handleSnackbar(true, `${error.message}`, "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    dispatch(getAccessToken());
  }, []);

  const resetFormDetails = () => {
    quickOrderForm.resetForm();
    quickOrderForm.setValues(initialValues);
  };

  const handleItem = (value: "add" | "remove", id: string = "") => {
    if (value === "add") {
      const newProduct = {
        ...productsInitialValue,
        id: uuidv4(),
      };
      const products = [...quickOrderForm.values.products, newProduct];
      quickOrderForm.setValues({
        ...quickOrderForm.values,
        products,
      });
    } else {
      const products = quickOrderForm.values.products.filter(
        (item) => item.id !== id
      );
      quickOrderForm.setValues({
        ...quickOrderForm.values,
        products,
      });
    }
  };

  const getTotal = (): number => {
    const total = quickOrderForm.values.products.reduce(
      (acc, cur) => acc + cur.price * cur.qty,
      0
    );
    return total || 0;
  };

  const handleSearchCustomer = async (value: string) => {
    if (!value) {
      quickOrderForm.resetForm();
      setCustomerSearchResult([]);
      setCustomerDropDown(false);
      return;
    }
    const payload = {
      access_token: access_token,
      search_query: value,
      offset: 0,
    };

    try {
      setCustomerLoading(true);
      const response = await Customer.instance.searchCustomer(payload);
      const data = response.data.data;
      setCustomerSearchResult(data);
      setCustomerDropDown(true);
    } catch (error) {
      // handleSnackbar(true, error?.message, "error");
      console.error("Something went wrong", error);
    } finally {
      setCustomerLoading(false);
    }
  };

  const debouncedSearchCustomer = useCallback(
    debounce(handleSearchCustomer, 400),
    []
  );

  const handleCustomerClick = (customer: any) => {
    quickOrderForm.setValues((prevValues) => ({
      ...prevValues,
      display_name: customer.display_name || prevValues.display_name,
      email: customer.email || prevValues.email,
      qb_customer_id: customer.qb_customer_id || prevValues.qb_customer_id,
      phoneNo: customer.phoneNo || prevValues.phoneNo,
      address: customer.address || prevValues.address,
      city: customer.city || prevValues.city,
      country: customer.country || prevValues.country,
      zip: customer.zip || prevValues.zip,
      date: customer.date || prevValues.date,

    }));
    setCustomerDropDown(false);
  };

  const handleSearchProduct = async (query: string) => {
    try {
      setProductLoading(true);
      const response = await InFlow.instance.searchProducts(20, query);
      const data = response.data?.productsList;
      setProductSearchResult(data || []);
      setProductsDropDown(true);
    } catch (error) {
      console.error("Something went wrong", error);
    } finally {
      setProductLoading(false);
    }
  };

  const debouncedSearchProducts = useCallback(
    debounce(handleSearchProduct, 400),
    []
  );

  const handleResultClick = (result: any) => {
    
    const isDuplicate = quickOrderForm.values.products.some(
      (product: any) => product.item === result.name
    );

    if (!isDuplicate) {
      const newProduct = {
        ...productsInitialValue,
        id: uuidv4(),
        item: result.name,
        price: result.unitPrice,
        qty: 1,
      };

      // filter out any empty product if exists

      const updatedProducts = [
        ...quickOrderForm.values.products.filter((p) => p.item),
        newProduct,
      ];

      quickOrderForm.setValues({
        ...quickOrderForm.values,
        products: updatedProducts,
      });

      setProductsDropDown(false);
      setProductQuery("");
    } else {
      handleSnackbar(true, "Product already exists", "error");
    }
  };

  return (
    <Box component="form" sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          margin: "8px 0px",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1, marginRight: "12px" }}>
          <CustomInput
            value={quickOrderForm.values.invoiceNo}
            onChangeText={(value) =>
              quickOrderForm.setFieldValue("invoiceNo", value)
            }
            errorMessage={quickOrderForm.errors.invoiceNo as string}
            isError={
              (isValidString(quickOrderForm.errors.invoiceNo) &&
              quickOrderForm.touched.invoiceNo) ||
              false
            }
            handleBlur={quickOrderForm.handleBlur("invoiceNo")}
            required={false}
            label="Invoice #"
            placeholder="PTP000234"
            size="small"
            containerStyle={{ marginTop: "-1px", mb: "2px" }}
          />
        </Box>

        <Box sx={{ flex: 1, mr: 1 }}>
          <InputLabel
            sx={{ fontSize: 12, color: Colors.textColor, fontWeight: 700 }}
          >
            Status
          </InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            value={quickOrderForm.values.status}
            inputProps={{ "aria-label": "Without label" }}
            onChange={(e) =>
              quickOrderForm.setFieldValue("status", e.target.value)
            }
            fullWidth
            size="small"
            sx={{ mt: 0.5, pb: 0.2, height: "32px" }}
          >
            <MenuItem value={"In Process"}>In Process</MenuItem>
            <MenuItem value={"Completed"}>Completed</MenuItem>
          </Select>
        </Box>

        <Box sx={{ flex: 1 }}>
          <DatePicker
            customInput={
              <CustomInput
                value={quickOrderForm.values.date.toLocaleDateString() || ""}
                onChangeText={(value) => {
                  const parsedDate = new Date(value);
                  if (!isNaN(parsedDate.getTime())) {
                    quickOrderForm.setFieldValue("date", parsedDate);
                  }
                }}
                errorMessage=""
                isError={false}
                editable={false}
                handleBlur={quickOrderForm.handleBlur("date")}
                required={false}
                label="Due date"
                placeholder="Nov 3,2023"
                size="small"
                containerStyle={{ marginTop: "-1px" }}
                handleOnClick={() => setShowCalendar(true)}
              />
            }
            open={showCalendar}
            onClickOutside={() => setShowCalendar(false)}
            selected={quickOrderForm.values.date}
            onChange={(date) => {
              quickOrderForm.setFieldValue("date", date);
              setShowCalendar(false);
            }}
          />
        </Box>
      </Box>
      <Box>
        <CustomInput
          value={quickOrderForm.values.display_name}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("display_name", value);
            setCustomerQuery(value);
            debouncedSearchCustomer(value);
          }}
          errorMessage={quickOrderForm.errors.display_name as string}
          isError={
            (isValidString(quickOrderForm.errors.display_name) &&
              quickOrderForm.touched.display_name) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("display_name")}
          required={false}
          label="Customer Name"
          placeholder="John Doe"
          size="small"
        />

        <Box sx={{ position: "relative", marginTop: 1 }}>
          {customerLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            customerQuery &&
            customerDropDown && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  bgcolor: "#fff",
                  borderRadius: "0 0 25px 25px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  zIndex: 10,
                  maxHeight: "200px",
                  overflowY: "scroll",
                }}
              >
                <List>
                  {customerSearchResult.length > 0 ? (
                    customerSearchResult.map((result: any) => (
                      <ListItem
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                        button
                        key={result.qb_customer_id}
                        onClick={() => handleCustomerClick(result)}
                      >
                        <Typography>{result.display_name}</Typography>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <Typography>No results found</Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            )
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <CustomInput
          value={quickOrderForm.values.email}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("customerEmail", value);
          }}
          errorMessage={quickOrderForm.errors.email as string}
          isError={
            (isValidString(quickOrderForm.errors.email) &&
              quickOrderForm.touched.email) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("email")}
          required={false}
          label="Customer Email"
          placeholder="example@example.com"
          size="small"
          containerStyle={{ flex: 0.9, marginTop: 1 }}
        />

        <CustomInput
          value={quickOrderForm.values.phoneNo?.toString() || ""}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("phoneNo", value);
          }}
          errorMessage={quickOrderForm.errors.phoneNo as string}
          isError={
            (isValidString(quickOrderForm.errors.phoneNo) &&
              quickOrderForm.touched.phoneNo) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("phoneNo")}
          required={false}
          label="Phone"
          placeholder="(774)-222-2390"
          size="small"
          containerStyle={{ flex: 0.8, marginLeft: "8px", marginTop: 1 }}
          maxLength={13}
        />
      </Box>
      <Box style={{ margin: "8px 0px" }}>
        <CustomInput
          value={quickOrderForm.values.address}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("address", value);
          }}
          errorMessage={quickOrderForm.errors.address as string}
          isError={
            (isValidString(quickOrderForm.errors.address) &&
              quickOrderForm.touched.address) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("address")}
          required={false}
          label="Address"
          placeholder="Address"
          size="small"
          containerStyle={{ flex: 0.9, marginTop: 1 }}
        />
      </Box>
      <Box
        style={{
          display: "flex",
          gap: "5px",
          justifyContent: "space-between",
          marginTop: "-8px",
        }}
      >
        <CustomInput
          value={quickOrderForm.values.city}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("city", value);
          }}
          errorMessage={quickOrderForm.errors.city as string}
          isError={
            (isValidString(quickOrderForm.errors.city) &&
              quickOrderForm.touched.city) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("city")}
          required={false}
          label="City"
          placeholder="Shrewsbury"
          size="small"
          containerStyle={{ flex: 0.8 }}
        />

        <CustomInput
          value={quickOrderForm.values.country}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("country", value);
          }}
          errorMessage={quickOrderForm.errors.country as string}
          isError={
            (isValidString(quickOrderForm.errors.country) &&
              quickOrderForm.touched.country) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("country")}
          required={false}
          label="Country"
          placeholder="Shrewsbury"
          size="small"
          containerStyle={{ flex: 0.8 }}
        />

        <CustomInput
          value={quickOrderForm.values.zip}
          onChangeText={(value) => {
            quickOrderForm.setFieldValue("zip", value);
          }}
          errorMessage={quickOrderForm.errors.zip as string}
          isError={
            (isValidString(quickOrderForm.errors.zip) &&
              quickOrderForm.touched.zip) ||
            false
          }
          handleBlur={quickOrderForm.handleBlur("zip")}
          required={false}
          label="Zipcode"
          placeholder="01545"
          size="small"
          containerStyle={{ flex: 0.8 }}
        />
      </Box>

      <Box
        sx={{
          margin: "8px 0px",
          justifyContent: "space-between",
        }}
      >
        {quickOrderForm.values.products.map((product, index) => (
          <Box key={product.id}>
            <Box
              style={{
                flex: 8,
                display: "flex",
                justifyContent: "space-between",
              }}
              className="custom-product-row"
            >
              <Box sx={{ flex: 8 }} className="custom-item-row">
                <CustomInput
                  value={product.item || productQuery}
                  onChangeText={(value) => {
                    setProductQuery(value);
                    debouncedSearchProducts(value);
                  }}
                  errorMessage=""
                  isError={false}
                  handleBlur={() => {}}
                  required={false}
                  label="Item"
                  placeholder="Product Name or Number"
                  size="small"
                  containerStyle={{ flex: 14 }}
                  labelStyle={{ fontWeight: 400, color: "#666666" }}
                />
              </Box>

              <Box sx={{ mx: 1, flex: 2 }} className="custom-qty-number">
                <InputLabel sx={{ fontSize: 12, mb: "2px", fontWeight: 700 }}>
                  Qty
                </InputLabel>
                <CustomInput
                  aria-label="Demo number input"
                  placeholder="00"
                  size="small"
                  value={
                    quickOrderForm.values.products[index]?.qty?.toString() || ""
                  }
                  onChangeText={(value) => {
                    const products = quickOrderForm.values.products.map(
                      (it, ind) => {
                        if (ind === index) {
                          return {
                            ...it,
                            qty: parseInt(value) || 0,
                          };
                        } else return it;
                      }
                    );
                    quickOrderForm.setValues({
                      ...quickOrderForm.values,
                      products,
                    });
                  }}
                  errorMessage=""
                  isError={false}
                  handleBlur={() => {}}
                  required={false}
                />
              </Box>

              <Box sx={{ flex: 2 }} className="custom-price-row">
                <CustomInput
                  value={product.price.toString() || productQuery}
                  onChangeText={(value) => {
                    const products = quickOrderForm.values.products.map(
                      (it, ind) => {
                        if (ind === index) {
                          return {
                            ...it,
                            price: parseInt(value) || 0,
                          };
                        } else return it;
                      }
                    );
                    quickOrderForm.setValues({
                      ...quickOrderForm.values,
                      products,
                    });
                  }}
                  errorMessage=""
                  isError={false}
                  handleBlur={() => {}}
                  required={false}
                  label="Price"
                  placeholder="$50.00"
                  size="small"
                  // type="number"
                  labelStyle={{ fontWeight: 400, color: "#666666" }}
                  disabled={true}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              {quickOrderForm.values.products.length > 1 ? (
                <Button onClick={() => handleItem("remove", product.id)}>
                  <Typography
                    variant="h6"
                    component={"h6"}
                    color={"#2499E3"}
                    fontSize={"12px"}
                    className="clickable-text"
                  >
                    - Remove Item
                  </Typography>
                </Button>
              ) : null}
            </Box>
          </Box>
        ))}

        <Box sx={{ position: "relative", marginTop: 1 }}>
          {productLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            productQuery &&
            productsDropDown && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  bgcolor: "#fff",
                  borderRadius: "0 0 25px 25px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  zIndex: 10,
                  maxHeight: "200px",
                  overflowY: "scroll",
                }}
              >
                <List>
                  {productSearchResult.length > 0 ? (
                    productSearchResult.map((result: any, index: number) => (
                      <ListItem
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                        button
                        key={result.productId || result.productListingObjectId}
                        onClick={() => handleResultClick(result)}
                      >
                        <Typography>{result.name.trim()}</Typography>

                        <Typography>{result.unitPrice}</Typography>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <Typography>No results found</Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            )
          )}
        </Box>

        {quickOrderForm.values.products.length >= 1 ? (
          <Button onClick={() => handleItem("add")}>
            <Typography
              variant="h6"
              component={"h6"}
              color={"#2499E3"}
              fontSize={"12px"}
              className="clickable-text"
            >
              + Add Item
            </Typography>
          </Button>
        ) : null}
      </Box>

      <Box sx={{ width: "100%", display: "flex" }}>
        <Box sx={{ flex: 0.9, marginRight: "12px" }}>
          <InputLabel
            sx={{
              fontSize: 12,
              mb: "2px",
              color: Colors.textColor,
              fontWeight: 700,
            }}
          >
            Select Shipping
          </InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            inputProps={{ "aria-label": "Without label" }}
            value={quickOrderForm.values.shippingOptions}
            onChange={(e) => {
              quickOrderForm.setFieldValue("shippingOptions", e.target.value);
            }}
            fullWidth
            size="small"
          >
            <MenuItem value={"International"}>International</MenuItem>
            <MenuItem value={"Freight"}>Freight</MenuItem>
            <MenuItem value={"Pickup"}>Pickup</MenuItem>
          </Select>
        </Box>

        <Box sx={{ flex: 1 }}>
          <InputLabel
            sx={{
              fontSize: 12,
              mb: "2px",
              color: Colors.textColor,
              fontWeight: 700,
            }}
          >
            Select Payment Type
          </InputLabel>
          <Select
            labelId="demo-select-small-label"
            id="demo-select-small"
            inputProps={{ "aria-label": "Without label" }}
            value={quickOrderForm.values.paymentType}
            onChange={(e) => {
              quickOrderForm.setFieldValue("paymentType", e.target.value);
            }}
            fullWidth
            size="small"
          >
            <MenuItem value={"UPI"}>UPI</MenuItem>
            <MenuItem value={"Cash"}>Cash</MenuItem>
            <MenuItem value={"Credit/Debit Card"}>Credit/Debit Card</MenuItem>
          </Select>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "8px" }}>
        <div style={{ display: "flex" }}>
          <Typography
            variant="h6"
            component={"h1"}
            color={"#064061"}
            mr={"2px"}
            fontWeight={"normal"}
          >
            Total:
          </Typography>
          <Typography variant="h6" component={"h1"} color={"#064061"}>
            ${getTotal()}
          </Typography>
        </div>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2%",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          component={"h6"}
          color={"#2499E3"}
          fontSize={"12px"}
          className="clickable-text"
        >
          Add more details
        </Typography>

        <CustomEllipticalButton
          buttonSize="xs"
          buttonHeight="sm"
          label="Send Invoice"
          onBtnClick={() => {
          quickOrderForm.handleSubmit();
          }}
          disabled={quickOrderForm.isSubmitting}
          uppercase={false}
          variant="contained"
          buttonColor={Colors.secondary}
          labelColor={Colors.white}
        />
      </Box>
    </Box>
  );
}
